<template>
  <modal
    :header="true"
    :footer="false"
    :close-button="true"
    :wide="true"
    disable-click-outside
    class="social-display-modal"
    data-test="graphics-socialDisplayModal"
    @close="onCancel"
  >
    <template #modal-header>
      <h2>Select a Social Graphic</h2>
    </template>

    <template #modal-body>
      <div class="modal-body-wrapper">
        <div class="modal-controls">
          <div class="search-wrapper">
            <input
              v-model="searchQuery"
              type="search"
              class="search-input"
              placeholder="Search Social Graphics..."
              data-test="graphics-socialDisplay-search"
              @input="onSearchChange"
            >
          </div>
          <tags
            v-model="selectedTags"
            :allow-create="false"
            asset-type="themes"
            position="left"
          >
            <template #trigger="{ hasSelection, isOpen, toggle }">
              <button
                type="button"
                class="btn btn-sm btn-icon btn-style-b filter-tags-btn"
                :class="{ 'filter-tags-btn--active': isOpen || hasSelection }"
                data-test="graphics-socialDisplay-filterTags"
                @click="toggle"
              >
                <span class="far fa-bars-filter" />
              </button>
            </template>
          </tags>
        </div>

        <div
          v-if="selectedTags.length > 0"
          class="selected-tags-chips"
        >
          <div
            v-for="tag in selectedTags"
            :key="tag"
            class="tag-chip"
            :data-test="`graphics-socialDisplay-tag-${getTagName(tag)}`"
          >
            <span class="tag-chip__text">{{ getTagName(tag) }}</span>
            <button
              class="tag-chip__remove"
              :data-test="`graphics-socialDisplay-removeTag-${getTagName(tag)}`"
              @click="removeTag(tag)"
            >
              <span class="fas fa-times" />
            </button>
          </div>
        </div>

        <div
          v-if="loading && !displays.length"
          class="text-center padding-lg"
        >
          <span class="fas fa-spinner fa-spin" />
          Loading Social Graphics...
        </div>

        <div
          v-else-if="error && !displays.length"
          class="text-center padding-lg text-warning"
        >
          <p>{{ error }}</p>
        </div>

        <div
          v-else
          class="modal-content-wrapper"
        >
          <div
            ref="scrollContainer"
            class="displays-grid"
            @scroll="onScroll"
          >
            <!-- Create New Card - First Item -->
            <button
              type="button"
              class="display-card create-new-card"
              data-test="graphics-socialDisplay-createNew"
              @click="createNewDisplay"
            >
              <div class="display-card__thumbnail create-new-thumbnail">
                <span class="fas fa-plus create-icon" />
              </div>
              <div class="display-card__info">
                <div class="display-card__title">
                  Create new Social Graphic
                </div>
              </div>
            </button>

            <!-- Display Cards -->
            <label
              v-for="display in displays"
              :key="display._id"
              class="display-card"
              :class="{ 'selected': selectedDisplayId === display._id }"
              @dblclick="selectAndConfirm(display)"
            >
              <input
                v-model="selectedDisplayId"
                type="radio"
                name="social-display"
                :value="display._id"
                class="hidden-radio"
                :data-test="`graphics-socialDisplay-select-${display._id}`"
              >
              <div class="display-card__thumbnail">
                <img
                  v-if="display.preview_url"
                  :src="display.preview_url"
                  :alt="display.title"
                >

                <div
                  v-else
                  class="display-card__placeholder"
                >
                  <span class="fas fa-desktop" />
                </div>
              </div>
              <div class="display-card__info">
                <div class="display-card__title">
                  {{ display.title }}
                </div>

                <div class="display-card__subtitle">
                  {{ getLayoutLabel(display.layout) }}
                </div>
              </div>
            </label>

            <div
              v-if="loading && displays.length"
              class="loading-more"
            >
              <span class="fas fa-spinner fa-spin" />
            </div>
          </div>

          <div class="modal-footer-actions">
            <button
              class="btn btn-style-a btn-add-display"
              :disabled="!selectedDisplayId"
              data-test="graphics-socialDisplay-confirm"
              @click="confirmSelection"
            >
              Add Social Graphic
            </button>
          </div>
        </div>
      </div>
    </template>
  </modal>
</template>

<script>
import Modal from './Modal.vue';
import Tags from '../Tags.vue';
import Themes from '../../services/themes';

export default {
  emits: ['close', 'create-new', 'select'],
  name: 'SocialDisplayModal',
  components: {
    Modal,
    Tags,
  },
  data() {
    return {
      displays: [],
      loading: true,
      error: null,
      searchQuery: '',
      selectedDisplayId: null,
      currentPage: 1,
      totalDisplays: 0,
      isLoadingMore: false,
      requestId: 0,
      selectedTags: [],
    };
  },
  watch: {
    selectedTags() {
      this.loadDisplays(true);
    },
  },
  async mounted() {
    await this.loadDisplays();
  },
  methods: {
    async loadDisplays(reset = false) {
      const requestId = ++this.requestId; // eslint-disable-line

      try {
        this.error = null;

        if (reset) {
          this.displays = [];
          this.currentPage = 1;
        }

        this.loading = true;
        const response = await Themes.getThemes({
          page: this.currentPage,
          search: this.searchQuery || undefined,
          tags: this.selectedTags.length > 0
            ? this.selectedTags
            : undefined,
        });

        if (requestId !== this.requestId) { return; }

        if (reset) {
          this.displays = response.themes || [];
        } else {
          this.displays = [...this.displays, ...(response.themes || [])];
        }

        this.totalDisplays = response.total || 0;
      } catch (err) {
        if (requestId !== this.requestId) { return; }

        this.error = 'Failed to load social graphics. Please try again.';
        console.error('Error loading displays:', err);
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
        }
      }
    },

    async onSearchChange() {
      await this.loadDisplays(true);
    },

    getTagName(tag) {
      return typeof tag === 'string' ? tag : tag?.tagName || '';
    },

    removeTag(tag) {
      this.selectedTags = this.selectedTags.filter(
        (selectedTag) => selectedTag !== tag,
      );
    },

    async onScroll(e) {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const scrollThreshold = 100;

      if (
        scrollTop + clientHeight >= scrollHeight - scrollThreshold
        && !this.loading
        && !this.isLoadingMore
        && this.displays.length < this.totalDisplays
      ) {
        this.isLoadingMore = true;
        this.currentPage += 1;
        await this.loadDisplays();
        this.isLoadingMore = false;
      }
    },

    getLayoutLabel(layout) {
      if (!layout) return 'Social Graphic';

      const labels = {
        grid: 'Grid Layout',
        waterfall: 'Waterfall Layout',
        carousel: 'Carousel Layout',
        mural: 'Mural Layout',
        isolated: 'Isolated Layout',
        lowerthird: 'Lower Third Layout',
        lowerthirdscroll: 'Lower Third Layout',
        cinematic: 'Cinematic Layout',
        ticker: 'Ticker Layout',
      };

      return labels[layout] || layout.charAt(0).toUpperCase() + layout.slice(1);
    },

    onCancel() {
      this.$emit('close');
    },

    createNewDisplay() {
      this.$emit('create-new');
    },

    confirmSelection() {
      const display = this.displays.find((d) => d._id === this.selectedDisplayId);
      if (display) {
        this.$emit('select', display);
      }
    },

    selectAndConfirm(display) {
      // Set the selected display and immediately confirm
      this.selectedDisplayId = display._id;
      this.$emit('select', display);
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.modal-container) {
  max-width: 900px !important;
  width: 90vw !important;
}

:deep(.modal-body) {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.modal-body-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
}

.modal-content-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.modal-controls {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;
  flex-shrink: 0;
}

.search-wrapper {
  flex: 1;
}

.search-input {
  width: 100%;
  height: 40px;
  font-family: var(--font-family);
  font-feature-settings: var(--font-feature-settings, normal);
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.87);
  background-color: #ffffff;
  padding: .5rem 1rem;
  padding-left: 2.75rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 2rem;
  appearance: none;
  outline-color: transparent;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s, box-shadow 0.2s, background-size 0.2s cubic-bezier(0.64, 0.09, 0.08, 1);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cpath d='m21 21-4.35-4.35'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 0.875rem center;
  background-size: 1rem;

  &:focus {
    outline: none;
    border-color: #00b4cc;
    box-shadow: 0 0 0 3px rgba(0, 180, 204, 0.1);
  }
}

.filter-tags-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #dfeaef;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover,
  &:focus,
  &--active {
    background: #f0fdff;
    border-color: #00b4cc;
    color: #00b4cc;
    outline: none;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.15);
  }

  .fa-bars-filter {
    font-size: 16px;
  }
}

.selected-tags-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  background-color: #e0f7fa;
  border: 1px solid #00b4cc;
  border-radius: 16px;
  font-size: 0.875rem;
  color: #00838f;
  transition: all 0.2s ease;

  &__text {
    line-height: 1;
  }

  &__remove {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    color: #00838f;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    height: 1rem;
    font-size: 0.75rem;
    transition: color 0.2s ease;

    &:hover {
      color: #d32f2f;
    }
  }
}

.displays-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.5rem;
  flex: 1;
  min-height: 0;
  scrollbar-gutter: stable;
}

.loading-more {
  grid-column: 1 / -1;
  text-align: center;
  padding: 1rem;
  color: #64748b;
}

.display-card {
  cursor: pointer;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.75rem;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  background: white;
  overflow: visible;
  max-height: 195px;

  &:hover {
    border-color: #00b4cc;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &.selected {
    border-color: #00b4cc;
    box-shadow: 0 0 0 3px rgba(0, 180, 204, 0.15);
  }

  &__thumbnail {
    position: relative;
    width: 100%;
    height: 112.5px;
    background-color: #f8fafc;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 0.75rem;
    flex-shrink: 0;

    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #cbd5e1;

    .fas {
      font-size: 2.5rem;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  &__title {
    font-weight: 700;
    color: #1e293b;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
    margin-bottom: 0.25rem;
  }

  &__subtitle {
    font-size: 0.85rem;
    color: #64748b;
    font-weight: 400;
    line-height: 1.4;
    margin-top: auto;
  }
}

.create-new-card {
  border-style: dashed;
  border-color: #cbd5e1;
  background: #f8fafc;

  &:hover {
    border-color: #00b4cc;
    background: #f0fdff;
    transform: translateY(-2px);
  }

  .display-card__thumbnail {
    background: transparent;
    margin-bottom: 0.75rem;
    position: relative;
    height: 112.5px;
  }

  .display-card__info {
    text-align: center;
  }

  .display-card__title {
    color: #475569;
  }
}

.create-new-thumbnail {
  background: transparent;
}

.create-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 3rem;
  color: #cbd5e1;
}

.modal-footer-actions {
  display: flex;
  justify-content: flex-end;
  padding: 1.25rem 0 0.5rem 0;
  margin-top: auto;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-add-display {
  padding: 0.625rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.hidden-radio {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
</style>
